import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Shield, Mail, ArrowLeft, Users, Phone, Building2, User } from "lucide-react";
import CONFIG from "@/lib/config";
import { useToast } from "@/hooks/use-toast";

export function AccessDenied() {
  const { toast } = useToast();
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    message: ''
  });

  // Get user info from URL parameters only - no auth dependency
  const urlParams = new URLSearchParams(window.location.search);
  const emailFromUrl = urlParams.get('email');
  const nameFromUrl = urlParams.get('name');
  
  // Use URL params only
  const displayEmail = emailFromUrl || 'Unknown';
  const displayName = nameFromUrl || 'Unknown User';

  // Initialize form with URL params if available
  useEffect(() => {
    if (emailFromUrl) {
      setFormData(prev => ({ ...prev, email: emailFromUrl }));
    }
    if (nameFromUrl) {
      const nameParts = nameFromUrl.split(' ');
      if (nameParts.length >= 2) {
        setFormData(prev => ({ 
          ...prev, 
          firstName: nameParts[0], 
          lastName: nameParts.slice(1).join(' ') 
        }));
      } else {
        setFormData(prev => ({ ...prev, firstName: nameFromUrl }));
      }
    }
  }, [emailFromUrl, nameFromUrl]);

  const handleSubmitContactForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/access-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'request_contact',
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          companyName: formData.companyName,
          message: formData.message
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      toast({
        title: "Request Submitted",
        description: "Your request has been submitted successfully. Our team will contact you soon.",
      });

      setIsContactDialogOpen(false);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error",
        description: "Failed to submit your request. Please try again or contact support@tectonixai.com directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Card className="shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-3xl flex items-center justify-center mx-auto">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                Access Restricted
              </CardTitle>
              <CardDescription className="text-gray-600 text-base">
                Your account doesn't have permission to access this platform
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6 pt-0">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Access Required:</strong> Your account doesn't have access to this platform. Contact <strong>support@tectonixai.com</strong> to get a 7-day free trial.
              </AlertDescription>
            </Alert>

            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Current Account Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">{displayEmail}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium text-gray-900">{displayName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Permission Level:</span>
                  <span className="font-medium text-gray-900 capitalize">No permissions</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="text-red-600 font-medium">Access Denied</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <h4 className="font-semibold text-blue-900 mb-2">Get 7-Day Free Trial</h4>
                <p className="text-blue-800 text-sm mb-4">
                  Contact our support team at <strong>support@tectonixai.com</strong> to request a 7-day free trial and get access to the platform.
                </p>
                <div className="flex flex-row items-center justify-center gap-3">
                  <Button
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all flex-1"
                    onClick={() => setIsContactDialogOpen(true)}
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Request Contact
                  </Button>
                <Button
                    size="lg"
                  variant="outline"
                    className="text-green-700 border-green-300 hover:bg-green-50 font-semibold px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all flex-1"
                    onClick={() => {
                      const subject = encodeURIComponent('Request 7-Day Free Trial - Tectonix Procurement');
                      const body = encodeURIComponent(`Hello Tectonix Support Team,

I would like to request a 7-day free trial for the Tectonix Procurement platform.

Account Details:
- Email: ${displayEmail}
- Name: ${displayName}

I understand this is a free trial period and I will need to subscribe after the trial ends.

Thank you!`);
                      window.open(`mailto:support@tectonixai.com?subject=${subject}&body=${body}`);
                    }}
                >
                    <Mail className="w-5 h-5 mr-2" />
                    Send Email
                </Button>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
                <button
                  onClick={() => window.location.href = "/login"}
                  className="hover:text-blue-600 hover:underline transition-colors flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to Login
                </button>
                <span className="text-gray-400">•</span>
                <button
                  onClick={() => window.location.href = "/tenderer"}
                  className="hover:text-green-600 hover:underline transition-colors flex items-center gap-1"
                >
                  <Shield className="w-3 h-3" />
                  Tenderer Portal
                </button>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                Need help? Contact us at <a href="mailto:support@tectonixai.com" className="text-blue-600 hover:underline">support@tectonixai.com</a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Request Dialog */}
        <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Request Contact</DialogTitle>
              <DialogDescription>
                Fill out the form below and our team will contact you about getting access to the platform.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitContactForm} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="firstName"
                      className="pl-10"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="email"
                    type="email"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="phone"
                    type="tel"
                    className="pl-10"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="companyName"
                    className="pl-10"
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Tell us about your needs..."
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsContactDialogOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}